import { FlightlineEventReview } from "../models/MissionReview"
import { getDistanceInfo, getQCStatusForEvent } from "./MissionReviewUtils"

export interface SingleReviewSplit {
    newReview: FlightlineEventReview
    updatedReview: FlightlineEventReview
}

export interface DoubleReviewSplit {
    newReview: FlightlineEventReview
    updatedPreviousReview: FlightlineEventReview
    updatedNextReview: FlightlineEventReview
}

/**
 * Takes the first .2 nm from the next card
 */
export const addNewDistReviewFromNextCard = (
    nextReview: FlightlineEventReview,
    newFeReviewId: number,
): SingleReviewSplit => {
    const nextReviewDistInfo = nextReview.distance_info
    if (nextReviewDistInfo == null) {
        throw new Error("Impossible scenario! Unable to split review from the next card. No Start or Stop distances")
    }

    const startDistanceNM = nextReviewDistInfo.start_distance_nm + 0.01

    const newReview = updateOrCreateNewDistanceReview(nextReviewDistInfo.start_distance_nm, startDistanceNM, {
        eventReview: nextReview,
        newFeReviewId,
    })

    const updatedNextDistReview = updateOrCreateNewDistanceReview(
        startDistanceNM,
        nextReviewDistInfo.stop_distance_nm,
        { eventReview: nextReview },
    )

    return { newReview, updatedReview: updatedNextDistReview }
}

/**
 * Takes the last .2 nm from the previous card
 */
export const addNewDistReviewFromPrevCard = (
    prevReview: FlightlineEventReview,
    newFeReviewId: number,
): SingleReviewSplit => {
    const prevReviewDistInfo = prevReview.distance_info
    if (prevReviewDistInfo == null) {
        throw new Error("Impossible scenario! Unable to split review from the next card. No Start or Stop distances")
    }

    const stopDistanceNM = prevReviewDistInfo.stop_distance_nm - 0.01

    const updatedPrevDistReview = updateOrCreateNewDistanceReview(
        prevReviewDistInfo.start_distance_nm,
        stopDistanceNM,
        { eventReview: prevReview },
    )

    const newReview = updateOrCreateNewDistanceReview(stopDistanceNM, prevReviewDistInfo.stop_distance_nm, {
        eventReview: prevReview,
        newFeReviewId,
    })

    return { newReview, updatedReview: updatedPrevDistReview }
}

/**
 * Takes the last .1 nm from the previous card and the first .1 from the next card
 * - this is a rare occurrence and only if both the previous and next card only have 0.3 nm to choose from
 */

export const addNewDistReviewFromNextAndPrevCard = (
    prevReview: FlightlineEventReview,
    nextReview: FlightlineEventReview,
    newFeReviewId: number,
): DoubleReviewSplit => {
    const prevReviewDistInfo = prevReview.distance_info
    const nextReviewDistInfo = nextReview.distance_info
    if (prevReviewDistInfo == null || nextReviewDistInfo == null) {
        throw new Error("Impossible scenario! Unable to split review from the next card. No Start or Stop distances")
    }

    const startDistanceNM = prevReviewDistInfo.start_distance_nm + 0.01
    const stopDistanceNM = nextReviewDistInfo.stop_distance_nm - 0.01

    const updatedPreviousReview = updateOrCreateNewDistanceReview(
        prevReviewDistInfo.start_distance_nm,
        startDistanceNM,
        { eventReview: prevReview },
    )

    const newReview = updateOrCreateNewDistanceReview(startDistanceNM, stopDistanceNM, {
        eventReview: prevReview,
        newFeReviewId,
    })

    const updatedNextReview = updateOrCreateNewDistanceReview(stopDistanceNM, nextReviewDistInfo.stop_distance_nm, {
        eventReview: nextReview,
    })

    return { updatedPreviousReview, newReview, updatedNextReview }
}

const updateOrCreateNewDistanceReview = (
    startDistance: number,
    stopDistance: number,
    { eventReview, newFeReviewId }: { eventReview: FlightlineEventReview; newFeReviewId?: number },
): FlightlineEventReview => {
    const qc_status = getQCStatusForEvent(newFeReviewId != null, eventReview.qc_status)
    const qc_comment = newFeReviewId ? "" : eventReview.qc_comment

    return {
        ...eventReview,
        qc_status,
        qc_comment,
        // if we are adding a reviewId then this is a new card, therefore shouldn't have an id
        id: newFeReviewId ? undefined : eventReview.id,
        fe_review_id: newFeReviewId ?? eventReview.fe_review_id,

        distance_info: getDistanceInfo(startDistance, stopDistance),
    }
}
